import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails,
  Grid, TextField, FormControl, InputLabel, Select, MenuItem, Switch,
  FormControlLabel, Button, IconButton, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, Snackbar, Alert, Card, CardContent,
  Divider, Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Preview as PreviewIcon,
  DragIndicator as DragIcon,
  VisibilityOff as HiddenIcon,
  Visibility as VisibleIcon,
  RestartAlt as ResetIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { applicationFormConfig, getActiveFormConfig } from '../../config/applicationFormConfig';

// Serialize config to JSON — converts function-based `visible` to a sentinel string
const serializeConfig = (config) => {
  const clone = {
    ...config,
    steps: config.steps.map(step => ({
      ...step,
      fields: step.fields.map(field => ({
        ...field,
        visible: typeof field.visible === 'function' ? '__dynamic__' : field.visible
      }))
    }))
  };
  return JSON.stringify(clone);
};

const FormConfigAdmin = () => {
  const [config, setConfig] = useState(() => getActiveFormConfig());
  const [selectedStep, setSelectedStep] = useState(null);
  const [selectedField, setSelectedField] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'email', label: 'Email' },
    { value: 'tel', label: 'Phone' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select (Dropdown)' },
    { value: 'textarea', label: 'Textarea' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'radio', label: 'Radio Group' },
    { value: 'button', label: 'Button' },
  ];

  const handleSaveConfig = () => {
    localStorage.setItem('applicationFormConfig', serializeConfig(config));
    setSnackbar({ open: true, message: 'Configuration saved! Changes are live on the application form.', severity: 'success' });
  };

  const handleResetDefaults = () => {
    localStorage.removeItem('applicationFormConfig');
    setConfig(applicationFormConfig);
    setSnackbar({ open: true, message: 'Reset to default configuration.', severity: 'info' });
  };

  const handleAddStep = () => {
    setSelectedStep({ id: '', label: '', icon: 'Assignment', description: '', fields: [] });
    setDialogType('step');
    setDialogOpen(true);
  };

  const handleEditStep = (stepIndex) => {
    setSelectedStep({ ...config.steps[stepIndex], index: stepIndex });
    setDialogType('step');
    setDialogOpen(true);
  };

  const handleDeleteStep = (stepIndex) => {
    const newSteps = [...config.steps];
    newSteps.splice(stepIndex, 1);
    setConfig({ ...config, steps: newSteps });
  };

  const handleAddField = (stepIndex) => {
    setSelectedField({
      name: '', label: '', type: 'text', required: false, visible: true,
      gridSize: { xs: 12, sm: 6 },
      layout: { row: 1, column: 1, span: 1 },
      placeholder: '', helperText: '', options: [],
      stepIndex
    });
    setDialogType('field');
    setDialogOpen(true);
  };

  const handleEditField = (stepIndex, fieldIndex) => {
    const field = config.steps[stepIndex].fields[fieldIndex];
    setSelectedField({ ...field, stepIndex, fieldIndex });
    setDialogType('field');
    setDialogOpen(true);
  };

  const handleDeleteField = (stepIndex, fieldIndex) => {
    const newSteps = config.steps.map((s, i) => {
      if (i !== stepIndex) return s;
      const newFields = [...s.fields];
      newFields.splice(fieldIndex, 1);
      return { ...s, fields: newFields };
    });
    setConfig({ ...config, steps: newSteps });
  };

  const handleToggleFieldVisible = (stepIndex, fieldIndex) => {
    const field = config.steps[stepIndex].fields[fieldIndex];
    if (typeof field.visible === 'function') return; // dynamic — cannot toggle
    const newSteps = config.steps.map((s, si) => {
      if (si !== stepIndex) return s;
      return {
        ...s,
        fields: s.fields.map((f, fi) =>
          fi === fieldIndex ? { ...f, visible: !f.visible } : f
        )
      };
    });
    setConfig({ ...config, steps: newSteps });
  };

  const handleSaveStep = (stepData) => {
    const newSteps = [...config.steps];
    if (stepData.index !== undefined) {
      newSteps[stepData.index] = {
        id: stepData.id, label: stepData.label, icon: stepData.icon,
        description: stepData.description, fields: newSteps[stepData.index].fields
      };
    } else {
      newSteps.push({ id: stepData.id, label: stepData.label, icon: stepData.icon, description: stepData.description, fields: [] });
    }
    setConfig({ ...config, steps: newSteps });
    setDialogOpen(false);
  };

  const handleSaveField = (fieldData) => {
    const { stepIndex, fieldIndex, ...field } = fieldData;
    const newSteps = config.steps.map((s, si) => {
      if (si !== stepIndex) return s;
      const newFields = [...s.fields];
      if (fieldIndex !== undefined) {
        newFields[fieldIndex] = field;
      } else {
        newFields.push(field);
      }
      return { ...s, fields: newFields };
    });
    setConfig({ ...config, steps: newSteps });
    setDialogOpen(false);
  };

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination, type } = result;

    if (type === 'step') {
      const newSteps = [...config.steps];
      const [moved] = newSteps.splice(source.index, 1);
      newSteps.splice(destination.index, 0, moved);
      setConfig({ ...config, steps: newSteps });
    } else if (type === 'field') {
      const stepIndex = parseInt(source.droppableId.split('-')[1]);
      const newSteps = config.steps.map((s, si) => {
        if (si !== stepIndex) return s;
        const newFields = [...s.fields];
        const [moved] = newFields.splice(source.index, 1);
        newFields.splice(destination.index, 0, moved);
        return { ...s, fields: newFields };
      });
      setConfig({ ...config, steps: newSteps });
    }
  };

  const getFieldVisibilityStatus = (field) => {
    if (typeof field.visible === 'function') return 'dynamic';
    if (field.visible === false) return 'hidden';
    return 'visible';
  };

  const totalFields = config.steps.reduce((sum, s) => sum + s.fields.length, 0);
  const hiddenFields = config.steps.reduce((sum, s) =>
    sum + s.fields.filter(f => f.visible === false).length, 0);
  const isModified = localStorage.getItem('applicationFormConfig') !== null;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Form Configurator</Typography>
          <Typography variant="body2" color="text.secondary">
            {config.steps.length} steps · {totalFields} fields · {hiddenFields} hidden
            {isModified && <Chip label="Customized" size="small" color="warning" sx={{ ml: 1 }} />}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isModified && (
            <Tooltip title="Restore original default configuration">
              <Button variant="outlined" color="warning" startIcon={<ResetIcon />} onClick={handleResetDefaults}>
                Reset Defaults
              </Button>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => { setDialogType('preview'); setDialogOpen(true); }}
          >
            Preview
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSaveConfig}>
            Save & Publish
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 3 }}>
        Changes saved here take effect immediately on the public application form — no redeployment needed.
        Toggle the eye icon on any field to show or hide it. Drag steps and fields to reorder them.
      </Alert>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="steps" type="step">
          {(provided) => (
            <div {...provided.droppableProps} ref={provided.innerRef}>
              {config.steps.map((step, stepIndex) => (
                <Draggable key={step.id} draggableId={step.id} index={stepIndex}>
                  {(provided) => (
                    <Paper ref={provided.innerRef} {...provided.draggableProps} sx={{ mb: 2, borderRadius: 2 }} elevation={2}>
                      <Accordion defaultExpanded={stepIndex === 0}>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
                            <Box {...provided.dragHandleProps} sx={{ display: 'flex', color: 'text.secondary' }}>
                              <DragIcon />
                            </Box>
                            <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
                              {step.label}
                            </Typography>
                            <Chip size="small" label={`${step.fields.length} fields`} sx={{ mr: 1 }} />
                            {step.fields.filter(f => f.visible === false).length > 0 && (
                              <Chip size="small" icon={<HiddenIcon />}
                                label={`${step.fields.filter(f => f.visible === false).length} hidden`}
                                color="default" sx={{ mr: 1 }}
                              />
                            )}
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleEditStep(stepIndex); }}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={e => { e.stopPropagation(); handleDeleteStep(stepIndex); }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </AccordionSummary>

                        <AccordionDetails sx={{ pt: 0 }}>
                          {step.description && (
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                              {step.description}
                            </Typography>
                          )}

                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
                            <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => handleAddField(stepIndex)}>
                              Add Field
                            </Button>
                          </Box>

                          <Droppable droppableId={`fields-${stepIndex}`} type="field">
                            {(provided) => (
                              <div {...provided.droppableProps} ref={provided.innerRef}>
                                {step.fields.map((field, fieldIndex) => {
                                  const visStatus = getFieldVisibilityStatus(field);
                                  return (
                                    <Draggable
                                      key={`${field.name}-${fieldIndex}`}
                                      draggableId={`${step.id}-${field.name}-${fieldIndex}`}
                                      index={fieldIndex}
                                    >
                                      {(provided) => (
                                        <Card
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          sx={{
                                            mb: 1, borderRadius: 1,
                                            opacity: visStatus === 'hidden' ? 0.55 : 1,
                                            border: '1px solid',
                                            borderColor: visStatus === 'hidden' ? 'divider' : 'transparent',
                                            bgcolor: visStatus === 'hidden' ? 'grey.50' : 'background.paper'
                                          }}
                                        >
                                          <CardContent sx={{ py: '8px !important', px: 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                              <Box {...provided.dragHandleProps} sx={{ color: 'text.disabled', display: 'flex' }}>
                                                <DragIcon fontSize="small" />
                                              </Box>

                                              <Box sx={{ flexGrow: 1 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {field.label || <em style={{ color: '#999' }}>Untitled</em>}
                                                  </Typography>
                                                  <Typography variant="caption" color="text.secondary">
                                                    ({field.name})
                                                  </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                                  <Chip size="small" label={field.type} variant="outlined" />
                                                  {field.required && <Chip size="small" label="Required" color="error" />}
                                                  {visStatus === 'hidden' && <Chip size="small" icon={<HiddenIcon />} label="Hidden" color="default" />}
                                                  {visStatus === 'dynamic' && (
                                                    <Chip size="small" icon={<LockIcon />} label="Dynamic visibility" color="info" variant="outlined" />
                                                  )}
                                                  {field.options?.length > 0 && (
                                                    <Chip size="small" label={`${field.options.length} options`} variant="outlined" />
                                                  )}
                                                </Box>
                                              </Box>

                                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Tooltip title={visStatus === 'dynamic' ? 'Visibility is controlled dynamically' : visStatus === 'hidden' ? 'Show field' : 'Hide field'}>
                                                  <span>
                                                    <IconButton
                                                      size="small"
                                                      onClick={() => handleToggleFieldVisible(stepIndex, fieldIndex)}
                                                      disabled={visStatus === 'dynamic'}
                                                      color={visStatus === 'visible' ? 'primary' : 'default'}
                                                    >
                                                      {visStatus === 'hidden' ? <HiddenIcon fontSize="small" /> : <VisibleIcon fontSize="small" />}
                                                    </IconButton>
                                                  </span>
                                                </Tooltip>
                                                <IconButton size="small" onClick={() => handleEditField(stepIndex, fieldIndex)}>
                                                  <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton size="small" onClick={() => handleDeleteField(stepIndex, fieldIndex)}>
                                                  <DeleteIcon fontSize="small" />
                                                </IconButton>
                                              </Box>
                                            </Box>
                                          </CardContent>
                                        </Card>
                                      )}
                                    </Draggable>
                                  );
                                })}
                                {provided.placeholder}
                                {step.fields.length === 0 && (
                                  <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                                    <Typography variant="body2">No fields. Click "Add Field" to get started.</Typography>
                                  </Box>
                                )}
                              </div>
                            )}
                          </Droppable>
                        </AccordionDetails>
                      </Accordion>
                    </Paper>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <Box sx={{ mt: 2 }}>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddStep}>
          Add Step
        </Button>
      </Box>

      <StepDialog
        open={dialogOpen && dialogType === 'step'}
        step={selectedStep}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveStep}
      />

      <FieldDialog
        open={dialogOpen && dialogType === 'field'}
        field={selectedField}
        onClose={() => setDialogOpen(false)}
        onSave={handleSaveField}
        fieldTypes={fieldTypes}
      />

      <PreviewDialog
        open={dialogOpen && dialogType === 'preview'}
        config={config}
        onClose={() => setDialogOpen(false)}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar(p => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(p => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// ─── Step Dialog ────────────────────────────────────────────────────────────

const StepDialog = ({ open, step, onClose, onSave }) => {
  const [formData, setFormData] = useState({ id: '', label: '', icon: 'Assignment', description: '' });

  useEffect(() => {
    if (step) setFormData({ id: step.id || '', label: step.label || '', icon: step.icon || 'Assignment', description: step.description || '', index: step.index });
  }, [step]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{step?.index !== undefined ? 'Edit Step' : 'Add Step'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Step ID" value={formData.id}
              onChange={e => setFormData(p => ({ ...p, id: e.target.value }))}
              helperText="Unique identifier (e.g. student, medical)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Step Label" value={formData.label}
              onChange={e => setFormData(p => ({ ...p, label: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Icon" value={formData.icon}
              onChange={e => setFormData(p => ({ ...p, icon: e.target.value }))}
              helperText="MUI icon name (e.g. Person, School, Favorite)" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth multiline rows={2} label="Description" value={formData.description}
              onChange={e => setFormData(p => ({ ...p, description: e.target.value }))} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(formData)} variant="contained">Save</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Field Dialog ────────────────────────────────────────────────────────────

const FieldDialog = ({ open, field, onClose, onSave, fieldTypes }) => {
  const [formData, setFormData] = useState({
    name: '', label: '', type: 'text', required: false, visible: true,
    gridSize: { xs: 12, sm: 6 }, layout: { row: 1, column: 1, span: 1 },
    placeholder: '', helperText: '', options: []
  });
  const [newOption, setNewOption] = useState({ value: '', label: '' });
  const isDynamic = typeof field?.visible === 'function';

  useEffect(() => {
    if (field) {
      setFormData({
        name: field.name || '',
        label: field.label || '',
        type: field.type || 'text',
        required: field.required || false,
        visible: typeof field.visible === 'function' ? true : (field.visible !== false),
        gridSize: field.gridSize || { xs: 12, sm: 6 },
        layout: field.layout || { row: 1, column: 1, span: 1 },
        placeholder: field.placeholder || '',
        helperText: field.helperText || '',
        options: field.options || [],
        stepIndex: field.stepIndex,
        fieldIndex: field.fieldIndex,
      });
    }
  }, [field]);

  const handleAddOption = () => {
    if (newOption.value && newOption.label) {
      setFormData(p => ({ ...p, options: [...p.options, { ...newOption }] }));
      setNewOption({ value: '', label: '' });
    }
  };

  const handleRemoveOption = (index) => {
    setFormData(p => ({ ...p, options: p.options.filter((_, i) => i !== index) }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{field?.fieldIndex !== undefined ? 'Edit Field' : 'Add Field'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          {/* Identity */}
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Field Name (ID)" value={formData.name}
              onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
              helperText="Unique key stored with form data (no spaces)" />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Field Label" value={formData.label}
              onChange={e => setFormData(p => ({ ...p, label: e.target.value }))} />
          </Grid>

          {/* Type + toggles */}
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel>Field Type</InputLabel>
              <Select value={formData.type} label="Field Type"
                onChange={e => setFormData(p => ({ ...p, type: e.target.value }))}>
                {fieldTypes.map(t => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControlLabel
              control={<Switch checked={formData.required} onChange={e => setFormData(p => ({ ...p, required: e.target.checked }))} />}
              label="Required"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <Tooltip title={isDynamic ? 'Visibility is controlled dynamically by form logic' : ''}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.visible}
                    disabled={isDynamic}
                    onChange={e => setFormData(p => ({ ...p, visible: e.target.checked }))}
                  />
                }
                label={isDynamic ? 'Visible (dynamic)' : 'Visible on form'}
              />
            </Tooltip>
          </Grid>

          {/* Layout */}
          <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Layout</Typography></Divider></Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Row" value={formData.layout.row}
              onChange={e => setFormData(p => ({ ...p, layout: { ...p.layout, row: parseInt(e.target.value) || 1 } }))}
              inputProps={{ min: 1 }} helperText="Vertical row position" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Column" value={formData.layout.column}
              onChange={e => setFormData(p => ({ ...p, layout: { ...p.layout, column: parseInt(e.target.value) || 1 } }))}
              inputProps={{ min: 1 }} helperText="Horizontal column position" />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField fullWidth type="number" label="Column Span" value={formData.layout.span}
              onChange={e => setFormData(p => ({ ...p, layout: { ...p.layout, span: parseInt(e.target.value) || 1 } }))}
              inputProps={{ min: 1, max: 4 }} helperText="How many columns to span" />
          </Grid>

          {/* Grid size */}
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Width on mobile (xs)</InputLabel>
              <Select value={formData.gridSize.xs} label="Width on mobile (xs)"
                onChange={e => setFormData(p => ({ ...p, gridSize: { ...p.gridSize, xs: e.target.value } }))}>
                {[4, 6, 8, 12].map(s => <MenuItem key={s} value={s}>{s === 12 ? '12 — Full width' : s === 6 ? '6 — Half width' : s === 8 ? '8 — Two thirds' : '4 — One third'}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth>
              <InputLabel>Width on desktop (sm)</InputLabel>
              <Select value={formData.gridSize.sm} label="Width on desktop (sm)"
                onChange={e => setFormData(p => ({ ...p, gridSize: { ...p.gridSize, sm: e.target.value } }))}>
                {[2, 3, 4, 6, 8, 12].map(s => <MenuItem key={s} value={s}>{s} / 12 {s === 12 ? '— Full' : s === 6 ? '— Half' : s === 4 ? '— Third' : s === 3 ? '— Quarter' : ''}</MenuItem>)}
              </Select>
            </FormControl>
          </Grid>

          {/* Text properties */}
          <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Display</Typography></Divider></Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Placeholder" value={formData.placeholder}
              onChange={e => setFormData(p => ({ ...p, placeholder: e.target.value }))} />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField fullWidth label="Helper Text" value={formData.helperText}
              onChange={e => setFormData(p => ({ ...p, helperText: e.target.value }))} />
          </Grid>

          {/* Options for Select / Radio */}
          {(formData.type === 'select' || formData.type === 'radio') && (
            <>
              <Grid item xs={12}><Divider><Typography variant="caption" color="text.secondary">Options</Typography></Divider></Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth label="Value" value={newOption.value}
                  onChange={e => setNewOption(p => ({ ...p, value: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth label="Display Label" value={newOption.label}
                  onChange={e => setNewOption(p => ({ ...p, label: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button fullWidth variant="outlined" onClick={handleAddOption} sx={{ height: 56 }}>Add</Button>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {formData.options.map((opt, i) => (
                    <Chip key={i} label={`${opt.value} → ${opt.label}`} onDelete={() => handleRemoveOption(i)} variant="outlined" />
                  ))}
                  {formData.options.length === 0 && (
                    <Typography variant="caption" color="text.secondary">No options yet — add some above.</Typography>
                  )}
                </Box>
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={() => onSave(formData)} variant="contained">Save Field</Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Preview Dialog ──────────────────────────────────────────────────────────

const PreviewDialog = ({ open, config, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
    <DialogTitle>Form Preview</DialogTitle>
    <DialogContent>
      <Box sx={{ p: 1 }}>
        {config.steps.map((step) => {
          const visibleFields = step.fields.filter(f => f.visible !== false && typeof f.visible !== 'function');
          const hiddenFields = step.fields.filter(f => f.visible === false);
          return (
            <Paper key={step.id} sx={{ p: 3, mb: 3, borderRadius: 2 }} elevation={1}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>{step.label}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{step.description}</Typography>

              {/* Visible fields rendered in row groups */}
              {(() => {
                const byRow = {};
                visibleFields.forEach(f => {
                  const row = f.layout?.row || 1;
                  if (!byRow[row]) byRow[row] = [];
                  byRow[row].push(f);
                });
                return Object.keys(byRow).sort((a, b) => a - b).map(row => (
                  <Grid container spacing={2} key={row} sx={{ mb: 1 }}>
                    {byRow[row].sort((a, b) => (a.layout?.column || 1) - (b.layout?.column || 1)).map((field, i) => (
                      <Grid item xs={field.gridSize?.xs || 12} sm={field.gridSize?.sm || 6} key={i}>
                        <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 1, bgcolor: 'primary.50' }}>
                          <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>{field.label}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">{field.type}{field.required ? ' · required' : ''}</Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                ));
              })()}

              {/* Hidden fields summary */}
              {hiddenFields.length > 0 && (
                <Box sx={{ mt: 2, p: 1.5, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <HiddenIcon sx={{ fontSize: 14 }} />
                    {hiddenFields.length} hidden field{hiddenFields.length > 1 ? 's' : ''}: {hiddenFields.map(f => f.label).join(', ')}
                  </Typography>
                </Box>
              )}
            </Paper>
          );
        })}
      </Box>
    </DialogContent>
    <DialogActions>
      <Button onClick={onClose}>Close</Button>
    </DialogActions>
  </Dialog>
);

export default FormConfigAdmin;
